import { useEffect, useRef } from "react";

// Stripe-style animated mesh gradient background (canvas orbs + optional rotating light beams).
export default function MeshBackground({ palette, opacity = 0.3, beams = false, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h;
    const colors = palette || [[16,185,129],[6,182,212],[99,102,241],[4,120,87],[15,118,110]];
    const orbs = colors.map((c, i) => ({
      color: c,
      bx: 0.12 + 0.76 * ((i * 0.41 + 0.13) % 1),
      by: 0.18 + 0.64 * ((i * 0.57 + 0.29) % 1),
      r: 0.34 + 0.12 * (i % 3),
      sp: 0.00013 + 0.00006 * i,
      ph: i * 2.1,
    }));

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      w = rect.width; h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";
      for (const o of orbs) {
        const px = (o.bx + Math.sin(t * o.sp + o.ph) * 0.18) * w;
        const py = (o.by + Math.cos(t * o.sp * 1.3 + o.ph) * 0.14) * h;
        const radius = o.r * Math.max(w, h);
        const g = ctx.createRadialGradient(px, py, 0, px, py, radius);
        const [r, gg, b] = o.color;
        g.addColorStop(0, `rgba(${r},${gg},${b},${opacity})`);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [palette, opacity]);

  return (
    <div className={`pointer-events-none overflow-hidden ${className}`} data-testid="mesh-background">
      <canvas ref={ref} className="absolute inset-0 w-full h-full" />
      {beams && (
        <div
          className="absolute animate-beam"
          style={{
            left: "50%", top: "50%", width: "160%", height: "160%",
            background:
              "conic-gradient(from 0deg, transparent 0deg, rgba(16,185,129,0.08) 40deg, transparent 85deg, rgba(6,182,212,0.07) 170deg, transparent 210deg, rgba(99,102,241,0.08) 300deg, transparent 345deg)",
          }}
        />
      )}
    </div>
  );
}
